import React, { useRef, useEffect, useState } from "react";
import Select, {
  components,
  SingleValueProps,
  CSSObjectWithLabel,
  OptionProps,
  DropdownIndicatorProps,
} from "react-select";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import dropdownGreenStarIcon from '@/images/icons/dropdown-green-star-icon.svg';
import dropdownChevronIcon from '@/images/icons/dropdown-chevron-icon.svg';

export type RolloverDropdownProperties = {
  placeholder: string,
  isDisabled: boolean,
  isLoading?: boolean,
  value: any,
  options?: Array<{
    label: string,
    value: string | string[] | number | {periodId: number, startDate: string, endDate: string},
    icon?: React.ReactNode,
    subtitle?: string,
    tag?: string,
    style?: React.CSSProperties,
    className?: string,
    isDisabled?: boolean,
  }>,
  'aria-label': string,
  onChange: (value: any) => void,
  styles?: React.CSSProperties,
  entityLabel?: string,
  className?: string,
  size?: 'sm' | 'md' | 'lg',
  customColors?: {
    control?: string,
    focusBorder?: string,
    optionSelected?: string,
    optionHover?: string,
    optionText?: string,
    optionSelectedText?: string,
  },
  optionStyles?: React.CSSProperties,
  iconStyles?: React.CSSProperties,
  subtitleStyles?: React.CSSProperties,
  optionContentStyles?: React.CSSProperties,
}

function SingleValue({
  children,
  ...props
}: SingleValueProps<any>) {
  if (!props.data.icon && !props.data.tag) return <components.SingleValue {...props}>{children}</components.SingleValue>

  return (
    <components.SingleValue {...props}>
      <div className="d-flex align-items-center gap-2">
        {props.data.icon && <span>{props.data.icon}</span>}
        <div className="d-flex flex-column">
          <span>{props.data.label}</span>
          {props.data.tag && (
            <span style={{
              backgroundColor: '#E6FAF1',
              color: '#006539',
              padding: '0.125rem 0.25rem',
              borderRadius: '0.25rem',
              fontFamily: 'Roboto Condensed',
              fontSize: '0.75rem',
              fontWeight: '400',
              textTransform: 'uppercase',
              marginTop: '0.25rem',
              alignSelf: 'flex-start',
              display: 'flex',
              lineHeight: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.25rem',
            }}>
              <img src={dropdownGreenStarIcon} alt="dropdown green star" style={{ width: '0.875rem', height: '0.875rem' }} />
              {props.data.tag}
            </span>
          )}
        </div>
      </div>
    </components.SingleValue>
  );
}

function CustomOption(props: OptionProps<any>) {
  const { data, isSelected, isFocused, isDisabled } = props;
  const { customColors, size, optionContentStyles, iconStyles, subtitleStyles } = (props.selectProps as any);

  const isOptionDisabled = data.isDisabled || isDisabled;

  return (
    <components.Option {...props}>
      <div
        className="d-flex align-items-start"
        style={{
          padding: size === 'sm' ? '0.5rem 0.75rem' : size === 'lg' ? '0.875rem 1rem' : '0.625rem 0.75rem',
          borderRadius: 'var(--tag-radius, 0.25rem)',
          border: '1px solid var(--brand-amira-purple-light, #F2E6F5)',
          background: isOptionDisabled ? '#f8f9fa' :
                     isSelected ? customColors?.optionSelected || '#f2e6f5' :
                     isFocused ? customColors?.optionHover || '#fdf7ff' :
                     'var(--white-0, #FFF)',
          outline: 'none',
          userSelect: 'none',
          WebkitTapHighlightColor: 'transparent',
          cursor: isOptionDisabled ? 'not-allowed' : 'pointer',
          opacity: isOptionDisabled ? 0.5 : 1,
          pointerEvents: isOptionDisabled ? 'none' : 'auto',
          ...optionContentStyles,
        }}
      >
        {data.icon && (
          <span style={{
            marginRight: "0.5rem",
            marginTop: "0.5rem",
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: isOptionDisabled ? 0.5 : 1,
            ...iconStyles
          }}>
            {data.icon}
          </span>
        )}
        <div className="d-flex flex-column flex-grow-1">
          <span style={{
            color: isOptionDisabled ? '#999' : 'inherit'
          }}>{data.label}</span>
          {data.tag && (
            <span style={{
              backgroundColor: isOptionDisabled ? '#e9ecef' : '#E6FAF1',
              color: isOptionDisabled ? '#6c757d' : '#006539',
              padding: '0.125rem 0.25rem',
              borderRadius: '0.25rem',
              fontFamily: 'Roboto Condensed',
              fontSize: '0.75rem',
              fontWeight: '400',
              textTransform: 'uppercase',
              marginTop: '0.25rem',
              alignSelf: 'flex-start',
              display: 'flex',
              lineHeight: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.25rem',
            }}>
              <img
                src={dropdownGreenStarIcon}
                alt="dropdown green star"
                style={{
                  width: '0.875rem',
                  height: '0.875rem',
                  opacity: isOptionDisabled ? 0.5 : 1
                }}
              />
              {data.tag}
            </span>
          )}
          {data.subtitle && (
            <small style={{
              color: isOptionDisabled ? '#999' : '#495665',
              fontSize: '0.875rem',
              fontWeight: '300',
              marginTop: '0.25rem',
              lineHeight: 'normal',
              fontFamily: 'Inter',
              ...subtitleStyles
            }}>
              {data.subtitle}
            </small>
          )}
        </div>
      </div>
    </components.Option>
  );
}

function CustomDropdownIndicator(props: DropdownIndicatorProps<any>) {
  const { selectProps } = props;
  const isOpen = selectProps.menuIsOpen;

  return (
    <components.DropdownIndicator {...props}>
      <img
        src={dropdownChevronIcon}
        alt="dropdown chevron"
        style={{
          width: '1.5rem',
          height: '1.5rem',
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease'
        }}
      />
    </components.DropdownIndicator>
  );
}

export const RolloverDropdown: React.FC<RolloverDropdownProperties> = ({
  isDisabled = false,
  placeholder = "",
  value = null,
  options = [],
  isLoading = false,
  'aria-label': ariaLabel,
  onChange,
  styles = {},
  entityLabel,
  className,
  size,
  customColors,
  optionStyles,
  iconStyles,
  subtitleStyles,
  optionContentStyles
}) => {
  const { t } = useTranslation(['reports']);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const selectRef = useRef<any>(null);

  // Close dropdown when it goes outside container bounds
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null;

    const checkBounds = () => {
      if (!isMenuOpen) return;

      // Throttle the checks to avoid excessive calls
      if (timeoutId) return;

      timeoutId = setTimeout(() => {
        const actionBody = document.querySelector('.action-body');
        const dropdownMenu = document.querySelector('.rolloverDropdown__menu');

        if (actionBody && dropdownMenu) {
          const containerRect = actionBody.getBoundingClientRect();
          const menuRect = dropdownMenu.getBoundingClientRect();

          const buffer = 500;
          const isOutside =
            menuRect.top < (containerRect.top - buffer) ||
            menuRect.bottom > (containerRect.bottom + buffer) ||
            menuRect.left < (containerRect.left - buffer) ||
            menuRect.right > (containerRect.right + buffer);

          if (isOutside) {
            setIsMenuOpen(false);
          }
        }
        timeoutId = null;
      }, 50);
    };

    const handleClickOutside = (event: MouseEvent) => {
      const modal = document.querySelector('.modal-content, .modal-dialog, [role="dialog"], .modal, .modal-body');
      if (modal && !modal.contains(event.target as Node) && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    const actionBody = document.querySelector('.action-body');
    if (actionBody) {
      actionBody.addEventListener('scroll', checkBounds, { passive: true });
    }

    document.addEventListener('click', handleClickOutside);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (actionBody) {
        actionBody.removeEventListener('scroll', checkBounds);
      }
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMenuOpen]);

  const dropdownStyle = cx('rolloverDropdown', className);

  // ensure label of selected value matches option labels
  const clonedValue = value ? { ...value } : value;
  const selectedOption = options.find(({value}) => value === clonedValue?.value);
  if (selectedOption) {
    Object.assign(value, {
      label: selectedOption.label,
      tag: selectedOption.tag,
      icon: selectedOption.icon,
      subtitle: selectedOption.subtitle
    });
  }

  const formatOptionLabel = (
    {value, label}: {value: any, label: string}
  ) => {
    if (value === "all") {
      return t("whole_class");
    }
    return label;
  };

  // Function to check if an option is disabled
  const isOptionDisabled = (option: any) => {
    return option.isDisabled === true;
  };

  const customStyles = {
    container: (base: CSSObjectWithLabel) => ({
      ...base,
    }),
    menu: (base: any) => ({
      ...base,
      margin: 0,
      borderRadius: '0rem 0rem 0.25rem 0.25rem',
      border: '1px solid #e1a9f0',
      boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
      borderBottom: 'none',
      zIndex: 1057,
    }),
    control: (base: CSSObjectWithLabel, state: any): CSSObjectWithLabel => ({
      ...base,
      minHeight: size === 'sm' ? '2.5rem' : size === 'lg' ? '3.5rem' : '3rem',
      borderColor: state.menuIsOpen ? '#e1a9f0' :
      state.isHovered ? '#e1a9f0' :
      customColors?.control || '#F2E6F5',
      boxShadow: state.menuIsOpen ? `0 0 0 1px #e1a9f0` : 'none',
      borderRadius: state.menuIsOpen ? '0.25rem 0.25rem 0 0' : 'var(--tag-radius, 0.25rem)',
      border: state.menuIsOpen ? '1px solid #e1a9f0' :
      state.isHovered ? '1px solid #e1a9f0' :
      '1px solid var(--brand-amira-purple-light, #F2E6F5)',
      padding: '0.5rem 0.25rem 0.5rem 0.25rem',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      ...styles,
    }),
    indicatorSeparator: (base: any) => ({
      ...base,
      display: 'none',
    }),
    menuList: (base: any) => ({
      ...base,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      maxHeight: '20rem',
      padding: '0.5rem',
      border: '1px solid #e1a9f0',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      scrollbarWidth: 'thin',
      scrollbarColor: '#e1a9f0 transparent',
      '&::-webkit-scrollbar': {
        width: '4px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'transparent',
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#e1a9f0',
        borderRadius: '2px',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: '#d299e6',
      },
    }),
    singleValue: (base: any) => ({
      ...base,
      padding: '0.5rem',
      borderRadius: 'var(--tag-radius, 0.25rem)',
      border: '1px solid var(--brand-amira-purple-light, #F2E6F5)',
      background: 'var(--white-0, #FFF)',
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: 'transparent',
      color: 'inherit',
      fontSize: size === 'sm' ? '0.875rem' : size === 'lg' ? '1.125rem' : '1rem',
      padding: '0',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      outline: 'none',
      userSelect: 'none',
      WebkitTapHighlightColor: 'transparent',
      '&:active': { backgroundColor: 'transparent' },
      ...optionStyles,
      ...state.data?.style,
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 1057,
    }),
  };

  return (
    <>
      { entityLabel && <span className="entityLabel">{entityLabel}</span> }
      <Select
        ref={selectRef}
        menuPlacement="auto"
        menuPosition="fixed"
        menuPortalTarget={document.body}
        isSearchable={false}
        className={dropdownStyle}
        classNamePrefix="rolloverDropdown"
        value={value}
        onChange={onChange}
        options={options}
        formatOptionLabel={formatOptionLabel}
        placeholder={placeholder}
        isDisabled={isLoading || isDisabled}
        isOptionDisabled={isOptionDisabled}
        aria-label={ariaLabel || ''}
        styles={customStyles}
        menuIsOpen={isMenuOpen}
        onMenuOpen={() => setIsMenuOpen(true)}
        onMenuClose={() => setIsMenuOpen(false)}
        components={{
          SingleValue,
          Option: CustomOption,
          DropdownIndicator: CustomDropdownIndicator
        }}
      />
    </>
  );
};